import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import './Suppliers.css';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_PAGE_SIZE = 25;

async function getToken(instance) {
  try {
    const accounts = instance.getAllAccounts();
    if (!accounts.length) return null;
    const resp = await instance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return resp.idToken;
  } catch {
    const accounts = instance.getAllAccounts();
    return accounts[0]?.idToken || null;
  }
}

const COLUMNS = [
  { key: 'ma_number', label: 'Marketing Authorisation Number', param: 'maNumber', placeholder: 'Filter MA number...' },
  { key: 'product_name', label: 'Product Name', param: 'productName', placeholder: 'Filter product...' },
  { key: 'atc', label: 'ATC', param: 'atc', placeholder: 'Filter ATC...' },
  { key: 'active_substance', label: 'Active Substance', param: 'activeSubstance', placeholder: 'Filter substance...' },
];

const DETAIL_FIELDS = [
  { key: 'ma_number', label: 'Marketing Authorisation Number' },
  { key: 'product_name', label: 'Product Name' },
  { key: 'active_substance', label: 'Active Substance' },
  { key: 'atc', label: 'ATC' },
  { key: 'excipients', label: 'Excipients' },
  { key: 'pharmaceutical_form', label: 'Pharmaceutical Form' },
  { key: 'route_of_administration', label: 'Route of Administration' },
  { key: 'availability', label: 'Availability' },
  { key: 'ma_holder', label: 'MA Holder' },
  { key: 'registration_date', label: 'Registration Date' },
  { key: 'procedure_number', label: 'Procedure Number' },
  { key: 'additional_monitoring', label: 'Additional Monitoring' },
  { key: 'smpc_filename', label: 'SmPC' },
  { key: 'leaflet_filename', label: 'Patient Leaflet' },
  { key: 'par_filename', label: 'Public Assessment Report' },
];

const EMPTY_FILTERS = Object.fromEntries(COLUMNS.map(c => [c.key, '']));

export default function CbgPage() {
  const { instance } = useMsal();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const debounceRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const openDetail = useCallback(async (reg) => {
    setDetail(null);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/market-intelligence/cbg/detail?reg=${encodeURIComponent(reg)}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDetail(await res.json());
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, [instance]);

  const fetchData = useCallback(async (pg, flt) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ page: pg, pageSize: DEFAULT_PAGE_SIZE });
      for (const c of COLUMNS) {
        const v = (flt[c.key] || '').trim();
        if (v) params.set(c.param, v);
      }
      const res = await fetch(`${API}/api/market-intelligence/cbg?${params}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.rows || []);
      setTotal(json.total || 0);
    } catch (err) {
      setError(err.message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  // Fetch on page change (immediate)
  useEffect(() => { fetchData(page, filters); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced fetch on filter change — reset to page 1
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchData(1, filters);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFilters = COLUMNS.some(c => filters[c.key]);
  const pageCount = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * DEFAULT_PAGE_SIZE;
  const pageEnd = Math.min(safePage * DEFAULT_PAGE_SIZE, total);

  const paginationNums = (() => {
    const win = 5;
    let s = Math.max(1, safePage - Math.floor(win / 2));
    let e = Math.min(pageCount, s + win - 1);
    s = Math.max(1, e - win + 1);
    const nums = [];
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  })();

  const inputStyle = { width: '100%', padding: '5px 10px', fontSize: 12.5, border: '1px solid #d0d7de', borderRadius: 6, background: 'var(--card)', color: 'var(--t1)', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px', background: 'var(--surf)' }}>
      <h4 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--t1)' }}>CBG Medicines Database</h4>
      <div style={{ marginBottom: 14, fontSize: 11.5, color: 'var(--t3)' }}>
        Dutch Medicines Evaluation Board (CBG-MEB) registered products.
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {COLUMNS.map(c => (
          <div key={c.key} style={{ minWidth: 160, flex: 1 }}>
            <div style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>{c.label}</div>
            <input
              style={inputStyle}
              placeholder={c.placeholder}
              value={filters[c.key]}
              onChange={e => setFilters(f => ({ ...f, [c.key]: e.target.value }))}
            />
          </div>
        ))}
        {hasFilters && (
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, color: 'var(--teal)', background: 'var(--card)', border: '1px solid var(--teal)', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
          >Clear filters</button>
        )}
      </div>

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          Failed to load data: {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading...</div>
      )}

      {!loading && !error && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh' }}>
            <table className="sup-table" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  {COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} style={{ padding: 32, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                      {hasFilters ? 'No rows match the current filters' : 'No data available'}
                    </td>
                  </tr>
                ) : rows.map((row, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--t3)' }}>{pageStart + i + 1}</td>
                    {COLUMNS.map(c => (
                      <td key={c.key} style={{ whiteSpace: 'nowrap' }}>
                        {c.key === 'product_name' ? (
                          <button
                            type="button"
                            onClick={() => openDetail(row.reg)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', cursor: 'pointer', textDecoration: 'none', fontSize: 'inherit', fontFamily: 'inherit', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                          >{row[c.key] ?? ''}</button>
                        ) : (row[c.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
            <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
              {paginationNums.map(n => (
                <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
              ))}
              <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
            </nav>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
              Showing {total === 0 ? 0 : pageStart + 1}–{pageEnd} of {total.toLocaleString('en-US')} rows
            </span>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {(detailLoading || detail || detailError) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={() => { setDetail(null); setDetailError(null); }} />
          <div style={{ position: 'relative', background: 'var(--card)', borderRadius: 12, border: '1px solid #eef3f7', width: '95%', maxWidth: 640, maxHeight: '85vh', overflow: 'auto', padding: '20px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h5 style={{ margin: 0, fontWeight: 700, color: 'var(--t1)', fontSize: 15 }}>Product Detail</h5>
              <button type="button" onClick={() => { setDetail(null); setDetailError(null); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--t3)', lineHeight: 1 }}>×</button>
            </div>

            {detailLoading && <div style={{ padding: 30, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading...</div>}
            {detailError && <div style={{ padding: 16, color: '#991b1b', fontSize: 13 }}>Failed to load detail: {detailError}</div>}

            {detail && (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {DETAIL_FIELDS.map(f => {
                      const val = detail[f.key];
                      if (val == null || val === '') return null;
                      const isLink = typeof val === 'string' && val.startsWith('http');
                      return (
                        <tr key={f.key}>
                          <td style={{ padding: '6px 10px 6px 0', fontWeight: 600, color: 'var(--t2)', whiteSpace: 'nowrap', verticalAlign: 'top', width: '40%' }}>{f.label}</td>
                          <td style={{ padding: '6px 0', color: 'var(--t1)', wordBreak: 'break-word' }}>
                            {isLink ? <a href={val} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>{f.label}</a> : val}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {detail.cbg_link && (
                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <a
                      href={detail.cbg_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', padding: '7px 16px', fontSize: 13, fontWeight: 500, color: 'white', background: 'var(--teal)', borderRadius: 6, textDecoration: 'none' }}
                    >Open on CBG</a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
